import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { messagingChannelConfigsService } from "@/services/messaging-channel-configs-service";
import {
  CommunicationChannelKind,
  MessagingChannelConfig,
  UpsertMessagingChannelConfigRequest,
} from "@/types/messaging-channel-config";

export const messagingChannelConfigKeys = {
  all: ["messagingChannelConfigs"] as const,
  list: (hotelId: string) =>
    [...messagingChannelConfigKeys.all, "list", hotelId] as const,
};

export const useMessagingChannelConfigs = (hotelId: string | null) =>
  useQuery<MessagingChannelConfig[]>({
    queryKey: messagingChannelConfigKeys.list(hotelId ?? ""),
    queryFn: () => messagingChannelConfigsService.list(hotelId as string),
    enabled: !!hotelId,
    staleTime: 5 * 60 * 1000,
  });

const invalidateList = (
  qc: ReturnType<typeof useQueryClient>,
  hotelId: string,
): Promise<void> =>
  qc.invalidateQueries({
    queryKey: messagingChannelConfigKeys.list(hotelId),
  });

interface UpsertArgs {
  channel: CommunicationChannelKind;
  payload: UpsertMessagingChannelConfigRequest;
}

export const useUpsertMessagingChannelConfig = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<MessagingChannelConfig, unknown, UpsertArgs>({
    mutationFn: ({ channel, payload }) =>
      messagingChannelConfigsService.upsert(hotelId, channel, payload),
    onSuccess: () => invalidateList(qc, hotelId),
  });
};

export const useEnableMessagingChannel = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<MessagingChannelConfig, unknown, CommunicationChannelKind>(
    {
      mutationFn: (channel) =>
        messagingChannelConfigsService.enable(hotelId, channel),
      onSuccess: () => invalidateList(qc, hotelId),
    },
  );
};

export const useDisableMessagingChannel = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<MessagingChannelConfig, unknown, CommunicationChannelKind>(
    {
      mutationFn: (channel) =>
        messagingChannelConfigsService.disable(hotelId, channel),
      onSuccess: () => invalidateList(qc, hotelId),
    },
  );
};

export const useSetDefaultMessagingChannel = (hotelId: string) => {
  const qc = useQueryClient();
  return useMutation<MessagingChannelConfig, unknown, CommunicationChannelKind>(
    {
      mutationFn: (channel) =>
        messagingChannelConfigsService.setDefault(hotelId, channel),
      onSuccess: () => invalidateList(qc, hotelId),
    },
  );
};
